import React, { useState, useEffect } from 'react';
import { Building2, Users, Car, TrendingUp, Plus, AlertCircle, Clock, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { vehiclesAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalVehicles: 0,
    activeVehicles: 0,
    todayRevenue: 0,
    todayDeliveredCount: 0,
    yesterdayRevenue: 0,
    yesterdayDeliveredCount: 0,
    pendingVehicles: 0
  });
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStaffData();
  }, []);

  const loadStaffData = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Staff için sadece kendi lokasyonlarındaki araçları getir
      if (user?.locations && user.locations.length > 0) {
        const allVehicles = [];
        const allDeliveredVehicles = [];

        // Her lokasyon için aktif araçları getir
        for (const location of user.locations) {
          try {
            const vehiclesResponse = await vehiclesAPI.getActiveVehicles(location.id);
            if (vehiclesResponse.data.success) {
              allVehicles.push(...vehiclesResponse.data.data);
            }
          } catch (error) {
            console.warn(`Failed to load vehicles for location ${location.id}:`, error);
          }

          // Teslim edilmiş araçları da getir (son 24 saat)
          try {
            const deliveredResponse = await vehiclesAPI.getDeliveredVehicles(location.id);
            if (deliveredResponse.data.success) {
              allDeliveredVehicles.push(...deliveredResponse.data.data);
            }
          } catch (error) {
            console.warn(`Failed to load delivered vehicles for location ${location.id}:`, error);
          }
        }

        setVehicles(allVehicles);

        // Tarih hesaplamaları
        const today = new Date();
        const todayStr = today.toDateString();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toDateString();

        // Bugün gelen araçlar (check_in bugün olan aktif araçlar)
        const todayActiveVehicles = allVehicles.filter(v =>
          new Date(v.check_in).toDateString() === todayStr
        );

        // Bugün teslim edilen araçlar (check_out bugün olan delivered araçlar)
        const todayDeliveredVehicles = allDeliveredVehicles.filter(v =>
          v.check_out && new Date(v.check_out).toDateString() === todayStr
        );

        // Dün teslim edilen araçlar
        const yesterdayDeliveredVehicles = allDeliveredVehicles.filter(v =>
          v.check_out && new Date(v.check_out).toDateString() === yesterdayStr
        );

        // Bugün toplam ciro: aktif araçlar + bugün teslim edilenler
        const todayRevenue =
          todayActiveVehicles.reduce((sum, v) => sum + (parseFloat(v.actual_amount) || 0), 0) +
          todayDeliveredVehicles.reduce((sum, v) => sum + (parseFloat(v.actual_amount) || 0), 0);

        // Dün toplam ciro
        const yesterdayRevenue = yesterdayDeliveredVehicles.reduce((sum, v) => sum + (parseFloat(v.actual_amount) || 0), 0);

        setStats({
          totalVehicles: allVehicles.length,
          activeVehicles: allVehicles.filter(v =>
            ['received', 'washing', 'ready'].includes(v.status)
          ).length,
          todayRevenue: todayRevenue,
          todayDeliveredCount: todayDeliveredVehicles.length,
          yesterdayRevenue: yesterdayRevenue,
          yesterdayDeliveredCount: yesterdayDeliveredVehicles.length,
          pendingVehicles: allVehicles.filter(v => v.status === 'received').length
        });
      }

    } catch (err) {
      console.error('Failed to load staff data:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'received': return 'Teslim Alındı';
      case 'washing': return 'Yıkanıyor';
      case 'parked': return 'Park Edildi';
      case 'ready': return 'Hazır';
      case 'called': return 'Çağrıldı';
      case 'delivered': return 'Teslim Edildi';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'received': return 'bg-blue-100 text-blue-800';
      case 'washing': return 'bg-yellow-100 text-yellow-800';
      case 'parked': return 'bg-purple-100 text-purple-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'called': return 'bg-orange-100 text-orange-800';
      case 'delivered': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getServiceTypeText = (type) => {
    switch (type) {
      case 'both': return 'Vale + Yıkama';
      case 'wash': return 'Oto Yıkama';
      case 'valet': return 'Vale';
      default: return type;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-end items-center">
        <div className="flex space-x-3">
          <Link to="/vehicles/new" className="btn-primary flex items-center">
            <Plus className="w-4 h-4 mr-2" />
            Araç Girişi
          </Link>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-red-50 rounded-lg">
                <Car className="h-6 w-6 text-[#7a2c2c]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Toplam Araç</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalVehicles}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Aktif Araç</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeVehicles}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Bugün Ciro</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₺{stats.todayRevenue?.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">{stats.todayDeliveredCount} araç teslim edildi</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Dün Ciro</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₺{stats.yesterdayRevenue?.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">{stats.yesterdayDeliveredCount} araç teslim edildi</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Bekleyen Araç</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingVehicles}</p>
                <p className="text-xs text-gray-500">Teslim alındı durumunda</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 mb-2">Bugün vs Dün</p>
              <div className="flex justify-between text-sm">
                <span>Ciro:</span>
                <span className={`font-medium ${stats.todayRevenue >= stats.yesterdayRevenue ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.todayRevenue >= stats.yesterdayRevenue ? '+' : ''}
                  ₺{(stats.todayRevenue - stats.yesterdayRevenue).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Araç:</span>
                <span className={`font-medium ${stats.todayDeliveredCount >= stats.yesterdayDeliveredCount ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.todayDeliveredCount >= stats.yesterdayDeliveredCount ? '+' : ''}
                  {stats.todayDeliveredCount - stats.yesterdayDeliveredCount}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600 mb-2">Ortalama Araç Değeri</p>
              <p className="text-xl font-bold text-gray-900">
                ₺{stats.todayDeliveredCount > 0 ? (stats.todayRevenue / stats.todayDeliveredCount).toFixed(2) : '0.00'}
              </p>
              <p className="text-xs text-gray-500">Bugün bazında</p>
            </div>
          </div>
        </div>
      </div>

      {/* Current vehicles */}
      <div className="card">
        <div className="card-header">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Güncel Araçlar</h3>
            <Link to="/vehicles" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              Tümünü Gör
            </Link>
          </div>
        </div>
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '18%' }} /> {/* Araç - Primary */}
                <col style={{ width: '20%' }} /> {/* Müşteri */}
                <col style={{ width: '15%' }} /> {/* Hizmet */}
                <col style={{ width: '15%' }} /> {/* Durum */}
                <col style={{ width: '15%' }} /> {/* Park Yeri */}
                <col style={{ width: '17%' }} /> {/* Tutar */}
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Araç
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Müşteri
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Hizmet
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Durum
                  </th>
                  <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Park Yeri
                  </th>
                  <th scope="col" className="px-6 py-3.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Tutar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {vehicles.slice(0, 10).map((vehicle) => (
                  <tr key={vehicle.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-red-100 to-red-50 rounded-lg flex items-center justify-center border border-red-200">
                          <Car className="h-5 w-5 text-[#7a2c2c]" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">{vehicle.plate_number}</div>
                          <div className="text-xs text-gray-500">{vehicle.brand} {vehicle.model}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">{vehicle.customer_name}</div>
                        <div className="text-xs text-gray-500 flex items-center mt-0.5">
                          <Phone className="h-3 w-3 mr-1" />
                          {vehicle.customer_phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getServiceTypeText(vehicle.service_type)}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(vehicle.status)}`}>
                        {getStatusText(vehicle.status)}
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-900">
                      {vehicle.parking_spot || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-gray-900 tabular-nums">
                        ₺{parseFloat(vehicle.actual_amount || 0).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {vehicles.length === 0 && (
            <div className="text-center py-8">
              <Car className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz araç bulunmuyor</h3>
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Link to="/vehicles" className="card hover:shadow-md transition-shadow">
          <div className="card-body text-center">
            <Car className="h-12 w-12 text-[#7a2c2c] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Araç Yönetimi</h3>
            <p className="text-gray-600">Araçları görüntüle ve yönet</p>
          </div>
        </Link>

        <Link to="/reports" className="card hover:shadow-md transition-shadow">
          <div className="card-body text-center">
            <TrendingUp className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Raporlar</h3>
            <p className="text-gray-600">Detaylı raporları görüntüle</p>
          </div>
        </Link>

        <Link to="/settings" className="card hover:shadow-md transition-shadow">
          <div className="card-body text-center">
            <Building2 className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ayarlar</h3>
            <p className="text-gray-600">Hesap ayarlarını yönet</p>
          </div>
        </Link>

      </div>
    </div>
  );
};

export default Dashboard;